import React from 'react';
import {
  Box,
  Dialog,
  HStack,
  Portal,
  Stack,
  Table,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ReactComponent as Error } from 'assets/icons/errorIcon.svg';
import dayjs from 'dayjs';
import { useDeleteSession, useSessions } from 'hooks';
import { useTranslation } from 'localization';
import { Session } from 'types';

import {
  Button,
  DIALOG_ACTION_BUTTON_PROPS,
  IconButton,
  SERVICE_FLOW_BORDER,
  ServiceDialogBody,
  ServiceDialogCloseTrigger,
  ServiceDialogFooter,
  ServiceDialogHeader,
  serviceDialogShellProps,
  Spinner,
} from 'components';

import BorderedBox from '../../../ProfileTab/components/BorderedBox';

const Sessions: React.FC = () => {
  const { t } = useTranslation('component.settings.security');
  const { open, onOpen, onClose } = useDisclosure();
  const [sessionId, setSessionId] = React.useState<string>('');

  const { data: sessions, refetch: refreshSessions, isLoading } = useSessions();
  const { mutateAsync: deleteSession } = useDeleteSession();

  const extractBrowserName = (userAgent: string) => {
    const browserRegex = /(Chrome|Safari|Firefox|Edge|Opera)\/[\d.]+/;
    const match = browserRegex.exec(userAgent);
    return match ? match[1] : 'Unknown';
  };

  const closeHandler = () => {
    onClose();
    setSessionId('');
    refreshSessions();
  };

  const columnHelper = createColumnHelper<Session>();

  const columns = [
    columnHelper.accessor('user_agent', {
      id: 'browser',
      header: () => t('sessionBrowser'),
      cell: (info) => extractBrowserName(info.getValue()),
    }),
    columnHelper.accessor('user_agent', {
      id: 'system',
      header: () => t('sessionSystem'),
      cell: (info) => {
        const ua = info.getValue();
        const inner = ua.split('(')[1];
        return inner ? inner.split(';')[0].trim() : '—';
      },
    }),
    columnHelper.accessor('ip', {
      header: () => t('sessionIp'),
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('updated_at', {
      id: 'updated_at',
      header: () => t('sessionLastActive'),
      cell: (info) =>
        `${dayjs(info.getValue()).format('HH:mm')} · ${dayjs(
          info.getValue()
        ).format('YYYY-MM-DD')}`,
    }),
  ];

  const table = useReactTable({
    data: sessions?.list || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const rows = table.getRowModel().rows;
  const content = (() => {
    if (isLoading) {
      return (
        <Stack w="100%" align="center" justify="center" minH="160px">
          <Spinner />
        </Stack>
      );
    }

    if (rows.length === 0) {
      return (
        <Text fontSize="sm" color="body2" py={2}>
          {t('noSessions')}
        </Text>
      );
    }

    return (
      <Box
        w="100%"
        overflowX="auto"
        rounded="md"
        borderWidth="1px"
        borderColor={SERVICE_FLOW_BORDER}
        bg="dark"
      >
        <Table.Root size="sm" bg="dark" color="body" variant="line">
          <Table.Header>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Row
                key={headerGroup.id}
                bg="dark"
                borderBottomWidth="1px"
                borderBottomColor={SERVICE_FLOW_BORDER}
                _hover={{ bg: 'dark' }}
              >
                {headerGroup.headers.map((header) => (
                  <Table.ColumnHeader
                    key={header.id}
                    color="body2"
                    borderWidth={0}
                    py={3}
                    px={2}
                    fontSize="sm"
                    fontWeight="medium"
                    whiteSpace="nowrap"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Table.ColumnHeader>
                ))}
                <Table.ColumnHeader
                  w="3rem"
                  color="body2"
                  borderWidth={0}
                  py={3}
                  px={1}
                  fontSize="sm"
                  fontWeight="medium"
                  textAlign="end"
                >
                  {' '}
                </Table.ColumnHeader>
              </Table.Row>
            ))}
          </Table.Header>
          <Table.Body>
            {rows.map((row) => (
              <Table.Row
                key={row.id}
                bg="dark"
                borderBottomWidth="1px"
                borderBottomColor={SERVICE_FLOW_BORDER}
                transition="background 0.15s ease"
                _hover={{ bg: 'rgba(255, 255, 255, 0.04)' }}
              >
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell
                    key={cell.id}
                    fontSize="sm"
                    borderWidth={0}
                    py={3}
                    px={2}
                    verticalAlign="middle"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
                <Table.Cell
                  borderWidth={0}
                  py={2}
                  px={1}
                  textAlign="end"
                  verticalAlign="middle"
                >
                  <IconButton
                    ariaLabel={t('closeSession')}
                    icon={Error}
                    size="sm"
                    onClick={() => {
                      setSessionId(row.original.id);
                      onOpen();
                    }}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
    );
  })();

  return (
    <BorderedBox title={t('deviceManagement')} w="100%">
      {content}
      <Dialog.Root
        size="md"
        open={open}
        placement="center"
        onOpenChange={(e: { open: boolean }) => {
          if (!e.open) {
            closeHandler();
          }
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content {...serviceDialogShellProps()}>
              <ServiceDialogHeader>{t('closeSession')}</ServiceDialogHeader>
              <ServiceDialogCloseTrigger />
              <ServiceDialogBody>
                <Stack gap={3}>
                  <Text fontSize="sm" color="body2" lineHeight="tall">
                    {t('closeSessionConfirm')}
                  </Text>
                  {sessions?.current_session_id === sessionId && (
                    <Box
                      borderLeftWidth="3px"
                      borderLeftColor="warning"
                      bg="rgba(255, 150, 77, 0.08)"
                      px={3}
                      py={2}
                      borderRadius="md"
                    >
                      <Text fontSize="sm" color="warning">
                        {t('closeSessionCurrentWarning')}
                      </Text>
                    </Box>
                  )}
                </Stack>
              </ServiceDialogBody>
              <ServiceDialogFooter>
                <HStack justifyContent="flex-end" gap={2} flexWrap="wrap">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      onClose();
                      setSessionId('');
                    }}
                    title={t('cancel')}
                    {...DIALOG_ACTION_BUTTON_PROPS}
                  />
                  <Button
                    onClick={() =>
                      deleteSession({ id: sessionId }).then(() => {
                        closeHandler();
                      })
                    }
                    variant="filled"
                    title={t('closeSession')}
                    {...DIALOG_ACTION_BUTTON_PROPS}
                  />
                </HStack>
              </ServiceDialogFooter>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </BorderedBox>
  );
};

export default Sessions;
